/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { Box, useTheme } from '@mui/material';

const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];

const UsageChart = ({ data }) => {
  const theme = useTheme();

  const usageData = useMemo(
    () => [
      {
        name: 'In',
        data: data.receive ? data.receive.map(({ value }) => value) : [],
      },
      {
        name: 'Out',
        data: data.transmit ? data.transmit.map(({ value }) => value) : [],
      },
    ],
    [data],
  );

  const maxVal = Math.max(...usageData.flatMap((d) => d.data));

  const { divisor, unit } = useMemo(() => {
    let div = 1;
    let unitIdx = 0;
    let maxBits = maxVal * 8;

    while (maxBits >= 1024 && unitIdx < units.length - 1) {
      maxBits /= 1024;
      div *= 1024;
      unitIdx += 1;
    }

    return { divisor: div, unit: units[unitIdx] };
  }, [maxVal]);

  const xLables = useMemo(() => (data.receive || data.transmit || [])?.map(({ timeStamp }) => timeStamp), [data]);

  const options = useMemo(
    () => ({
      chart: {
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      colors: ['#7255E3', '#75A0AE'],
      dataLabels: { enabled: false },
      theme: { mode: theme.palette.mode },
      fill: {
        type: 'gradient',
        gradient: {
          opacityFrom: 0.6,
          opacityTo: 0.8,
        },
      },
      stroke: {
        colors: ['#7255E3', '#75A0AE'],
        curve: 'straight',
        width: 2,
      },
      grid: {
        show: false,
      },
      legend: { show: false },
      xaxis: {
        type: 'datetime',
        categories: xLables,
        min: xLables[0],
        tickAmount: 8,
        labels: {
          style: {
            colors: theme.palette.text.secondary,
          },
          datetimeUTC: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme.palette.text.secondary,
          },
          formatter: (value) => ((value * 8) / divisor).toFixed(2),
        },
        title: {
          text: unit,
          style: { color: theme.palette.text.secondary },
        },
      },
      tooltip: {
        enabled: true,
        x: {
          format: 'dd MMM yyyy HH:mm',
        },
        y: {
          formatter: (value) => `${((value * 8) / divisor).toFixed(2)} ${unit}`,
        },
      },
    }),
    [xLables],
  );

  if (!xLables.length) return null;

  return (
    <Box sx={{ border: '1px solid #E3E6E8', borderRadius: 1 }}>
      <Chart options={options} series={usageData} type="area" height="100%" width="100%" />
    </Box>
  );
};

UsageChart.propTypes = {
  data: PropTypes.object,
};

export default UsageChart;
